import React, { useContext, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { PERMISSION, SERVER_DATE_FORMAT } from 'constants/logic';
import ScheduleFilterChips, {
  SCHEDULE_FILTER_KEY,
  getDefaultScheduleFilterKey,
  isScheduleFilterKeySelectable,
} from 'features/ScheduleFilters';
import { userHasPermission } from 'helpers/core';
import Segment from 'helpers/segment';
import { useIsScheduleListV2Enabled } from 'hooks/useIsScheduleListV2Enabled';
import { selectActiveVenue, selectSchedulesStartDate, selectUserPermissions } from 'selectors/general';

import VenueSchedulesContext from './Contexts/VenueSchedulesContext';
import { setScheduleListFilter } from './YieldManagement.reducers';
import { selectScheduleListFilter } from './YieldManagement.selectors';

const SCHEDULE_FILTER_KEY_VALUES = Object.values(SCHEDULE_FILTER_KEY);
const SEGMENT_SCHEDULE_FILTER_CLICKED = 'Schedule Filter clicked';

function ScheduleFilterChipsContainer() {
  const dispatch = useDispatch();
  const selectedKey = useSelector(selectScheduleListFilter);
  const schedulesStartDate = useSelector(selectSchedulesStartDate);
  const venue = useSelector(selectActiveVenue);
  const permissions = useSelector(selectUserPermissions);
  const { filterCounts } = useContext(VenueSchedulesContext);
  const isScheduleListV2Enabled = useIsScheduleListV2Enabled();

  const { isManualStudio, venueId } = venue;
  const hasSpotManagePermission = userHasPermission({ permissions }, PERMISSION.DISPLAY_SPOT_MANAGEMENT);
  const dayKey = `${venueId}-${format(schedulesStartDate, SERVER_DATE_FORMAT)}`;
  const previousDayKeyRef = useRef(dayKey);
  const defaultKey = getDefaultScheduleFilterKey(filterCounts || {}, isManualStudio);
  const showNewClients = isScheduleListV2Enabled === true;
  const selectedKeyForChips = SCHEDULE_FILTER_KEY_VALUES.includes(selectedKey)
    ? selectedKey
    : defaultKey || SCHEDULE_FILTER_KEY.ACTIVE_CLASSES;
  const selectedKeyIsSelectable = isScheduleFilterKeySelectable(selectedKey, {
    counts: filterCounts || {},
    hasSpotManagePermission,
    isManualStudio,
    showNewClients,
  });

  useEffect(() => {
    // The counts empty out while the new day loads, which reads as an empty rail with no default.
    // Leaving the ref on the previous day keeps the change pending so the reset below runs against
    // the day's real counts instead of being spent on the gap.
    if (!defaultKey) {
      return;
    }

    const dayChanged = previousDayKeyRef.current !== dayKey;
    previousDayKeyRef.current = dayKey;

    // This effect runs before the schedules provider clears the old day's data, so a day change can
    // still be judged on the previous day's counts. Re-checking the selection against the counts in
    // hand catches the chip once its own day arrives, whichever order the two land in.
    if (dayChanged || !selectedKeyIsSelectable) {
      dispatch(setScheduleListFilter(defaultKey));
    }
  }, [dayKey, defaultKey, dispatch, selectedKeyIsSelectable]);

  function handleChange(filterKey) {
    Segment.event(SEGMENT_SCHEDULE_FILTER_CLICKED, {
      filterType: filterKey.toLowerCase(),
      venueId,
    });
    dispatch(setScheduleListFilter(filterKey));
  }

  return (
    <ScheduleFilterChips
      counts={filterCounts}
      hasSpotManagePermission={hasSpotManagePermission}
      isManualStudio={isManualStudio}
      selectedKey={selectedKeyForChips}
      showNewClients={showNewClients}
      onChange={handleChange}
    />
  );
}

export default ScheduleFilterChipsContainer;
